import { motion } from "framer-motion";
import { Headset, Link2, Megaphone } from "lucide-react";
import { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { cn } from "../../../lib/utils";
import { AvisoEmMassaDialog } from "../components/aviso-em-massa-dialog";
import { GerarLinkDialog } from "../components/gerar-link-dialog";
import { ReferenciaOperacoes } from "../components/referencia-operacoes";
import { STATUS_TAB } from "../components/status-colors";
import { TicketList } from "../components/ticket-list";
import { TicketPanel } from "../components/ticket-panel";
import { useFila, usePendenciasAtendimento } from "../hooks/use-suporte";
import { FilaStatus, STATUS_LABEL } from "../types";

// Rótulos curtos: as abas precisam caber lado a lado na coluna de 360px. Só
// os status da fila: ticket em triagem ou resolvido nela não chega ao atendente.
const ABAS: { status: FilaStatus; label: string }[] = [
  { status: "waiting_agent", label: "Fila" },
  { status: "open", label: "Atendendo" },
  { status: "closed", label: "Encerrados" },
];
const TODAS = "__todas__";

// Fila única de todas as ops: o filtro de ops é local, sobre os tickets já carregados.
export default function SuportePage() {
  const [status, setStatus] = useState<FilaStatus>("waiting_agent");
  const [ops, setOps] = useState(TODAS);
  const [selecionado, setSelecionado] = useState<string | null>(null);
  const [gerarAberto, setGerarAberto] = useState(false);
  const [avisoAberto, setAvisoAberto] = useState(false);

  const { data: fila, isLoading } = useFila(status);
  const { data: aguardando } = useFila("waiting_agent");

  const tickets = fila?.items ?? [];
  const opsDisponiveis = Array.from(new Set(tickets.map((t) => t.ops))).sort();
  const visiveis = ops === TODAS ? tickets : tickets.filter((t) => t.ops === ops);
  const qtdAguardando = aguardando?.items.length ?? 0;
  // Atendimentos em andamento com mensagem nova do entregador (aba Atendendo).
  const { data: qtdNovas = 0 } = usePendenciasAtendimento();
  const contagem: Partial<Record<FilaStatus, number>> = { waiting_agent: qtdAguardando, open: qtdNovas };

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col p-6">
      <motion.div
        className="mb-4 flex flex-wrap items-center justify-between gap-3"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2">
            <Headset className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Suporte</h1>
            <p className="text-muted-foreground">Atendimento aos entregadores</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setAvisoAberto(true)}>
            <Megaphone /> Aviso em massa
          </Button>
          <Button onClick={() => setGerarAberto(true)}>
            <Link2 /> Gerar link
          </Button>
        </div>
      </motion.div>

      <ReferenciaOperacoes />

      <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden rounded-lg border bg-card md:grid-cols-[360px_1fr]">
        <aside className="flex min-h-0 flex-col border-r">
          <div className="space-y-2 border-b p-3">
            <Tabs value={status} onValueChange={(v) => setStatus(v as FilaStatus)}>
              <TabsList className="grid w-full grid-cols-3">
                {ABAS.map(({ status: s, label }) => (
                  <TabsTrigger key={s} value={s} title={STATUS_LABEL[s]}
                    className={cn("min-w-0 px-1 text-xs", STATUS_TAB[s])}
                  >
                    <span className="truncate">
                      {label}
                      {(contagem[s] ?? 0) > 0 && ` (${contagem[s]})`}
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <Select value={ops} onValueChange={setOps}>
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODAS}>Todas as ops</SelectItem>
                {opsDisponiveis.map((o) => (
                  <SelectItem key={o} value={o}>
                    {o}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <TicketList tickets={visiveis} selectedId={selecionado} onSelect={setSelecionado} isLoading={isLoading} />
          </div>
        </aside>

        <section className="min-h-0">
          {selecionado ? (
            <TicketPanel key={selecionado} ticketId={selecionado} />
          ) : (
            <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
              Selecione um atendimento.
            </div>
          )}
        </section>
      </div>

      <GerarLinkDialog open={gerarAberto} onOpenChange={setGerarAberto} onGerado={setSelecionado} />
      <AvisoEmMassaDialog open={avisoAberto} onOpenChange={setAvisoAberto} />
    </div>
  );
}
